import { Button } from "design-system/atoms/Button"
import { SheetFooter } from "design-system/atoms/Sheet"
import { BusinessOpportunityDefinitionField } from "modules/capabilities/components/CapabilityDialog/BusinessOpportunityDefinitionField"
import {
  BusinessOpportunityRequiredFieldsField,
  RequiredField,
} from "modules/capabilities/components/CapabilityDialog/BusinessOpportunityRequiredFieldsField"
import { ReactToMessageFields } from "modules/capabilities/components/CapabilityDialog/ReactToMessageFields"
import { RequestReviewFields } from "modules/capabilities/components/CapabilityDialog/RequestReviewFields"
import { ContactRequirementFields } from "modules/capabilities/components/CapabilityDialog/ContactRequirementFields"
import { SupportFields } from "modules/capabilities/components/CapabilityDialog/SupportFields"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import {
  CapabilitiesResponseDataSchema,
  CapabilityIdentifier,
  ContactAdditionalField,
  ContactChannel,
  ReactToMessageCapabilityConfigSchema,
  SupportCapabilityConfigSchema,
  SupportEscalationStrategy,
} from "schema"
import z from "zod"
import { CapabilitySheetLayout } from "../../CapabilitySheetLayout"
import { createOverrideForm, OverrideFormActions } from "./createOverrideForm"

type CapabilityItem = z.infer<typeof CapabilitiesResponseDataSchema>[number]

const supportDefaultConfig = {
  handoffPolicy: "DeflectGracefully",
  escalationStrategy: SupportEscalationStrategy.OneTicketPerIssue,
  supportPhone: "",
  supportEmail: "",
  replyPolicyByChannel: {},
  contactChannel: "phoneOrEmail",
  additionalFields: [],
} satisfies z.infer<typeof SupportCapabilityConfigSchema>

const overrideFormByIdentifier = {
  [CapabilityIdentifier.BusinessOpportunity]: createOverrideForm(
    ({ value, onChange }) => (
      <div className="flex flex-col gap-6">
        <BusinessOpportunityDefinitionField
          value={value.definition}
          onChange={(definition) => onChange({ ...value, definition })}
        />
        <BusinessOpportunityRequiredFieldsField
          value={(value.requiredFields ?? []).map((f: RequiredField) => ({
            ...f,
            type: f.type ?? "string",
            required: f.required !== false,
          }))}
          onChange={(requiredFields: RequiredField[]) => onChange({ ...value, requiredFields })}
        />
        <ContactRequirementFields
          value={{ contactChannel: value.contactChannel, additionalFields: value.additionalFields }}
          onChange={(contact) => onChange({ ...value, ...contact })}
        />
      </div>
    ),
    {
      definition: "",
      requiredFields: [] as RequiredField[],
      contactChannel:
        ContactChannel.PhoneOrEmail as (typeof ContactChannel)[keyof typeof ContactChannel],
      additionalFields: [] as ContactAdditionalField[],
    },
  ),
  [CapabilityIdentifier.ReactToMessage]: createOverrideForm(
    ReactToMessageFields,
    { emojis: "❤️", definition: "" },
    (raw) => ReactToMessageCapabilityConfigSchema.parse(raw),
  ),
  [CapabilityIdentifier.Support]: createOverrideForm(
    SupportFields,
    supportDefaultConfig,
    undefined,
    (capability) =>
      (capability.config ?? {}) as Partial<z.infer<typeof SupportCapabilityConfigSchema>>,
  ),
  [CapabilityIdentifier.RequestReview]: createOverrideForm(RequestReviewFields, {
    providers: [],
  }),
}

type Props = {
  capability: CapabilityItem
  campaignId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  capabilityName: string
}

export function CapabilityOverrideDialog({
  capability,
  campaignId,
  open,
  onOpenChange,
  capabilityName,
}: Props) {
  const { t } = useTranslation()
  const [overrideActions, setOverrideActions] = useState<OverrideFormActions | null>(null)

  const FormComponent =
    capability.identifier in overrideFormByIdentifier
      ? overrideFormByIdentifier[capability.identifier as keyof typeof overrideFormByIdentifier]
      : null

  if (!FormComponent) return null

  return (
    <CapabilitySheetLayout
      open={open}
      onOpenChange={onOpenChange}
      maxWidth="lg"
      title={t("campaigns.capabilities.override.dialogTitle", { name: capabilityName })}
      description={t("campaigns.capabilities.override.dialogDescription")}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
        <FormComponent
          capability={capability}
          campaignId={campaignId}
          onClose={() => onOpenChange(false)}
          onActionsChange={setOverrideActions}
        />
      </div>
      <SheetFooter className="flex shrink-0 flex-row justify-between gap-2 border-t bg-background p-5">
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          {t("shared.cancel")}
        </Button>
        <div className="flex gap-2">
          {overrideActions?.hasOverride && (
            <Button
              variant="outline"
              onClick={overrideActions.onDelete}
              disabled={overrideActions.isPending}
            >
              {t("campaigns.capabilities.override.delete")}
            </Button>
          )}
          <Button
            onClick={overrideActions?.onSave}
            disabled={!overrideActions || overrideActions.isPending}
          >
            {t("campaigns.capabilities.override.save")}
          </Button>
        </div>
      </SheetFooter>
    </CapabilitySheetLayout>
  )
}
